import { createContext } from "preact";
import { useContext, useMemo, useState, useCallback } from "preact/hooks";
import axios from "axios";
import toast from "react-hot-toast";
import { useProjectDetailPage } from "../store";

const isDev = import.meta.env.DEV;
const buildUrl = (p) => (isDev ? `http://localhost:3000${p}` : p);

const FloatingActionsContext = createContext(null);

export function FloatingActionsProvider({ children }) {
  const { project, fetchProject, applySessionHeader, role } = useProjectDetailPage();
  const [submitting, setSubmitting] = useState(false);

  const statusVal = useMemo(() => {
    const raw = typeof project?.status === "string"
      ? project?.status
      : project?.status?.name || project?.status?.code || project?.status?.value;
    return String(raw || "").toLowerCase();
  }, [project?.status]);

  const allPaymentsPaid = useMemo(() => {
    const list = Array.isArray(project?.payments) ? project.payments : [];
    return list.length > 0 && list.every(p => String(p?.status).toLowerCase() === "paid");
  }, [project?.payments]);

  const appsComplete = useMemo(() => {
    const apps = Array.isArray(project?.apps) ? project.apps : [];
    if (!apps.length) return false;
    return apps.every(a => {
      const tasks = Array.isArray(a?.tasks) ? a.tasks : [];
      if (!tasks.length) return false;
      return tasks.every(t => Number(t?.porcent ?? t?.percent ?? 0) >= 100);
    });
  }, [project?.apps]);

  const canShowConcludeProject = useMemo(() => {
    return statusVal === "in-develop" && allPaymentsPaid && appsComplete;
  }, [statusVal, allPaymentsPaid, appsComplete]);

  const canShowCancelProject = useMemo(() => {
    return statusVal !== "cancelled" && statusVal !== "completed";
  }, [statusVal]);

  const canShowBackToPlanning = useMemo(() => {
    return !new Set(["planning", "cancelled", "completed"]).has(statusVal);
  }, [statusVal]);
  
  const canShowConfirmPlanning = useMemo(() => {
    return statusVal === "planning"
  }, [statusVal]);

  const sessionCfg = useMemo(() => {
    if (typeof applySessionHeader === "function") return applySessionHeader();
    return {};
  }, [applySessionHeader]);

  const changeStatus = useCallback(async (targetStatus) => {
    const projectId = project?.id || project?._id;
    if (!projectId) return;

    setSubmitting(true);
    try {
      const url = buildUrl(`/${role}/api/projects/${projectId}/status/${encodeURIComponent(targetStatus)}`);
      await axios.get(url, sessionCfg);
      await fetchProject?.();
      switch (targetStatus) {
        case "completed": toast.success("Proyecto concluido con éxito."); break;
        case "cancelled": toast.success("Proyecto cancelado."); break;
        case "planning":  toast.success("Proyecto regresó a 'planning'."); break;
        default:          toast.success("Estatus actualizado."); break;
      }
    } catch (err) {
      console.error("changeStatus error:", err);
      toast.error("No fue posible actualizar el estatus del proyecto.");
    } finally {
      setSubmitting(false);
    }
  }, [project, role, sessionCfg, fetchProject]);

  const value = useMemo(
    () => ({
      submitting,
      canShowConcludeProject,
      canShowCancelProject,
      canShowBackToPlanning,
      canShowConfirmPlanning,
      changeStatus,
    }),
    [
      submitting,
      canShowConcludeProject,
      canShowCancelProject,
      canShowBackToPlanning,
      canShowConfirmPlanning,
      changeStatus,
    ]
  );

  return (
    <FloatingActionsContext.Provider value={value}>
      {children}
    </FloatingActionsContext.Provider>
  );
}

export function useFloatingActions() {
  const ctx = useContext(FloatingActionsContext);
  if (!ctx) throw new Error("useFloatingActions debe usarse dentro de <FloatingActionsProvider>");
  return ctx;
}